import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useUser } from "./UserContext";
import { useSession } from "../hooks/useSession";
import { useIconResolver } from "./IconResolverContext";
import {
    buildMenuSections,
    collectAllowedPaths,
    isMenuPathAllowed,
    normalizeMenuRowsFromMasterApi,
} from "../utils/menuAccess";

const API_MASTER_BASE_URL = "/api3004";

const MenuAccessContext = createContext(null);

function readAuthIdFromStorage(user) {
    if (user?.auth_id != null) return user.auth_id;
    try {
        const raw = localStorage.getItem("userInfo");
        if (!raw) return null;
        const u = JSON.parse(raw);
        return u?.auth_id ?? null;
    } catch {
        return null;
    }
}

export function MenuAccessProvider({ children }) {
    const { user } = useUser();
    const { endSession } = useSession();
    const { loadIconLibrariesForIconIds } = useIconResolver();
    const authId = readAuthIdFromStorage(user);

    const [menuRows, setMenuRows] = useState([]);
    const [menuLoadError, setMenuLoadError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (authId == null) {
            setMenuRows([]);
            setMenuLoadError(null);
            setLoading(false);
            return;
        }

        const token = localStorage.getItem("token");
        const controller = new AbortController();
        setLoading(true);
        setMenuLoadError(null);

        (async () => {
            try {
                const url = `${API_MASTER_BASE_URL}/m_menu_group_function_list?auth_id=${encodeURIComponent(authId)}`;
                const res = await fetch(url, {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    signal: controller.signal,
                });
                if (!res.ok) {
                    if (res.status === 403) {
                        endSession();
                        return;
                    }
                    setMenuRows([]);
                    setMenuLoadError("Failed to load menu");
                    return;
                }
                const json = await res.json();
                const rows = normalizeMenuRowsFromMasterApi(json?.data ?? []);
                setMenuRows(rows);
                const iconIds = rows.flatMap((r) => (r.icon_id ? [r.icon_id] : [])).filter(Boolean);
                if (iconIds.length > 0) loadIconLibrariesForIconIds(iconIds);
            } catch (err) {
                if (err.name === "AbortError") return;
                setMenuRows([]);
                setMenuLoadError("Failed to load menu");
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        })();

        return () => controller.abort();
    }, [authId, endSession, loadIconLibrariesForIconIds]);

    const allowedPaths = useMemo(() => collectAllowedPaths(menuRows), [menuRows]);
    const menuSections = useMemo(() => buildMenuSections(menuRows), [menuRows]);

    const isPathAllowed = useCallback(
        (pathname) => isMenuPathAllowed(pathname, allowedPaths),
        [allowedPaths]
    );

    const value = useMemo(
        () => ({
            menuRows,
            menuSections,
            menuLoadError,
            loading,
            allowedPaths,
            isPathAllowed,
        }),
        [menuRows, menuSections, menuLoadError, loading, allowedPaths, isPathAllowed]
    );

    return <MenuAccessContext.Provider value={value}>{children}</MenuAccessContext.Provider>;
}

export function useMenuAccess() {
    const ctx = useContext(MenuAccessContext);
    if (!ctx) {
        throw new Error("useMenuAccess must be used within MenuAccessProvider");
    }
    return ctx;
}
